
import {
  Component,
  Inject,
  Mixins,
  Prop,
  Provide
} from "vue-property-decorator";
import { mapState } from "vuex";
import { DataModules } from "@/store/modules/data";
import RequestPromises from "@/mixins/requestPromises";
import {
  IImport,
  IImportMapping,
  IImportProviderData,
  IImportProviderMethods,
  IImportStep,
  IStepMappingsProviderData,
  IStepMappingsProviderMethods
} from "@/models/imports";
import _ from "@/boot/lodash";
import { IModalComponent } from "@/models/modal";

@Component({
  computed: {
    ...mapState({
      mappings(state, getters) {
        return getters[`data/single`]({
          storeModule: DataModules.IMPORT_MAPPINGS,
          scope: this.dataScope
        });
      }
    })
  }
})
export default class StepMappingsProvider extends Mixins(RequestPromises) {
  // Inject

  @Inject({ from: "importProvider" })
  public readonly $importProvider!: () => IImportProviderData;

  @Inject({ from: "importMethods" })
  public readonly $importMethods!: IImportProviderMethods;

  // Props

  @Prop({ required: true })
  public readonly importId!: IImport["id"];

  @Prop({ required: true })
  public readonly stepId!: IImportStep["id"];

  // Data

  public readonly dataScope = `$step_${this.stepId}`;
  public readonly mappings!: IImportMapping[];
  public isLoading = true;
  public isReloading = false;
  public isProcessing = false;

  // Life-cycle Methods

  public render() {
    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $mappingData: this.mappingProvider(),
        $mappingMethods: this.mappingMethods
      })
    );
  }

  public created() {
    this.isLoading = true;
    this.getMappings();
  }

  public beforeDestroy() {
    this.$store.dispatch("data/binSingle", {
      scope: this.dataScope,
      storeModule: DataModules.IMPORT_MAPPINGS,
      vm: this
    });
  }

  // Provide

  @Provide()
  public mappingProvider(): IStepMappingsProviderData {
    return {
      isLoading: this.isLoading,
      isRequired: this.isRequired,
      isReloading: this.isReloading,
      mappings: this.mappingsArray
    };
  }

  @Provide()
  public get mappingMethods(): IStepMappingsProviderMethods {
    return {
      continue: this.continue,
      openMappingModal: this.openMappingModal,
      refreshData: this.refreshData,
      update: this.update
    };
  }

  // Computed

  public get $import() {
    return this.$importProvider();
  }

  public get mappingsArray() {
    if (!this.mappings) return [];
    return _.values(this.mappings);
  }

  public get isRequired() {
    return !!_.filter(this.mappings, m => m.required && !m.object_id).length;
  }

  // Methods

  public async getMappings(ignoreStored = false): Promise<void> {
    try {
      await this.$store.dispatch(`data/${DataModules.IMPORT_MAPPINGS}/list`, {
        importId: this.importId,
        stepId: this.stepId,
        payload: {
          vm: this,
          scope: this.dataScope,
          ignoreStored
        }
      });
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public handle404NotFound(replace = false) {
    return this.$router[replace ? "replace" : "push"]({
      name: "adminImports"
    });
  }

  public refreshData() {
    this.isReloading = true;
    return this.getMappings(true);
  }

  public async openMappingModal(
    mapping: IImportMapping
  ): Promise<IModalComponent> {
    const modal: IModalComponent = await this.$store.dispatch(
      "ui/open/slideModal",
      {
        config: {
          component: () =>
            import("@/components/app/admin/imports/selectMappingModal.vue"),
          canCancel: ["button"],
          parent: this,
          width: 540,
          props: {
            importId: this.importId,
            stepId: this.stepId,
            mapping
          },
          events: {
            onSelect: async (objectId: IImportMapping["object_id"]) => {
              try {
                await this.update(mapping.id, objectId);
                modal.close();
              } catch (error) {
                this.$store.dispatch("api/handleError", {
                  error,
                  vm: this
                });
              }
            }
          }
        }
      }
    );
    return modal;
  }

  public async update(
    mappingId: IImportMapping["id"],
    objectId: IImportMapping["object_id"]
  ): Promise<void> {
    try {
      this.isProcessing = true;
      await this.$store.dispatch(`data/${DataModules.IMPORT_MAPPINGS}/update`, {
        importId: this.importId,
        stepId: this.stepId,
        mappingId,
        payload: {
          vm: this,
          data: {
            object_id: objectId
          }
        }
      });
      this.refreshData();
      this.$importMethods.refreshData();
    } finally {
      this.isProcessing = false;
    }
  }

  public async continue() {
    try {
      this.isProcessing = true;
      await this.$store.dispatch(`data/${DataModules.IMPORT_MAPPINGS}/skip`, {
        importId: this.importId,
        stepId: this.stepId,
        payload: {
          vm: this
        }
      });
      this.refreshData();
      this.$importMethods.refreshData();
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isProcessing = false;
    }
  }
}
